"use client";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, api, loadTokens } from "@/lib/api";

/**
 * EPIC-044 part 4f — Flag button. Renders nothing when not signed in
 * (so anonymous viewers don't see a "report" CTA they can't use).
 *
 * On click:
 *   1. Optionally prompts for a reason (window.prompt — closed-beta
 *      simple shape; richer modal in a follow-up).
 *   2. POSTs to /v1/discussions/:id/flag (or .../replies/:replyId/flag).
 *   3. Disables the button + shows "Reported" inline.
 *
 * Idempotent server-side, so a double-click is harmless.
 */
type Props =
  | { kind: "discussion"; discussionId: string }
  | { kind: "reply"; discussionId: string; replyId: string };

export function FlagButton(props: Props) {
  const { t } = useTranslation();
  const [signedIn, setSignedIn] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSignedIn(loadTokens() !== null);
  }, []);

  if (!signedIn) return null;

  async function handleClick() {
    if (submitting || done) return;
    const reason =
      typeof window !== "undefined"
        ? (window.prompt(t("flagButton.reasonPrompt")) ?? "").slice(0, 500)
        : "";
    setSubmitting(true);
    setError(null);
    try {
      if (props.kind === "discussion") {
        await api.flagDiscussion(props.discussionId, {
          reason: reason || undefined,
        });
      } else {
        await api.flagDiscussionReply(props.discussionId, props.replyId, {
          reason: reason || undefined,
        });
      }
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("flagButton.errFailed"));
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <span className="text-[10px] text-[var(--color-text3)]">
        {t("flagButton.reported")}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={submitting}
      className="text-[10px] text-[var(--color-text3)] hover:text-[var(--color-text2)] disabled:opacity-50"
      aria-label={t("flagButton.aria")}
      title={error ?? t("flagButton.title")}
    >
      ⚠ {t("flagButton.label")}
    </button>
  );
}
